import { useEffect, useRef, useState } from "react";

const PRINTER_SERVER_URL = "http://127.0.0.1:17890/v1";
const TOKEN_STORAGE_KEY = "MenuPass_Totem@PrinterServerToken";
const PAIRING_STORAGE_KEY = "MenuPass_Totem@PrinterServerPairing";
const PAIRING_TIMEOUT_MS = 120000;

type Printer = {
  id: string;
  name: string;
  isDefault?: boolean;
};

export type ReceiptOrder = {
  clientName: string;
  totalAmount: number;
  items: Array<{
    name: string;
    amount: number;
    totalAmount: number;
  }>;
};

export type ReceiptPrintResult =
  | { status: "submitted"; orderReference: string; printerId: string }
  | {
    status: "printer-unavailable" | "pairing-required" | "failed";
    orderReference: string;
    stage: PrintFailureStage;
    httpStatus?: number;
    message: string;
  };

export const getOrderReference = (response: unknown): string | undefined => {
  if (!response) return undefined;
  if (typeof response === "string" || typeof response === "number") {
    return String(response);
  }
  if (Array.isArray(response)) {
    return response
      .map((entry) => getOrderReference(entry))
      .find((reference) => !!reference);
  }
  if (typeof response !== "object") return undefined;

  const value = response as Record<string, unknown>;
  const directReference =
    value.transactionId ||
    value.orderId ||
    value.clientOrderId ||
    value.id;
  if (
    typeof directReference === "string" ||
    typeof directReference === "number"
  ) {
    return String(directReference);
  }

  return getOrderReference(
    value.order || value.clientOrder || value.transaction || value.data
  );
};

type PrintFailureStage =
  | "pairing"
  | "authentication"
  | "discovery"
  | "printer-selection"
  | "submission";

type PairingAccessResponse = {
  requestId: string;
  status: "PENDING" | "APPROVED" | "DENIED" | "EXPIRED";
  expiresAtEpochMs: number;
  token?: string;
};

class PrinterServerError extends Error {
  stage: PrintFailureStage;

  httpStatus?: number;

  constructor(stage: PrintFailureStage, message: string, httpStatus?: number) {
    super(message);
    this.stage = stage;
    this.httpStatus = httpStatus;
  }
}

let pairingPromise: Promise<string> | null = null;
const completedOrderReferences = new Map<string, ReceiptPrintResult>();
const pendingOrderReferences = new Map<string, Promise<ReceiptPrintResult>>();

const delay = (milliseconds: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });

const waitForApproval = async (
  pairing: PairingAccessResponse,
  deadlineEpochMs: number
): Promise<PairingAccessResponse> => {
  if (
    pairing.status !== "PENDING" ||
    Date.now() >= pairing.expiresAtEpochMs
  ) {
    return pairing;
  }
  if (Date.now() >= deadlineEpochMs) {
    throw new PrinterServerError(
      "pairing",
      "Printer pairing approval timed out"
    );
  }
  await delay(500);
  const statusResponse = await fetch(
    `${PRINTER_SERVER_URL}/pairing-requests/${pairing.requestId}`
  );
  if (!statusResponse.ok) {
    throw new PrinterServerError(
      "pairing",
      `Printer access status failed (${statusResponse.status})`,
      statusResponse.status
    );
  }
  return waitForApproval(
    (await statusResponse.json()) as PairingAccessResponse,
    deadlineEpochMs
  );
};

const requestAccess = async (): Promise<string> => {
  let initialPairing: PairingAccessResponse | undefined;
  const storedPairing = localStorage.getItem(PAIRING_STORAGE_KEY);
  if (storedPairing) {
    try {
      const pending = JSON.parse(storedPairing) as PairingAccessResponse;
      if (Date.now() < pending.expiresAtEpochMs) {
        const statusResponse = await fetch(
          `${PRINTER_SERVER_URL}/pairing-requests/${pending.requestId}`
        );
        if (statusResponse.ok) {
          initialPairing =
            (await statusResponse.json()) as PairingAccessResponse;
        }
      }
    } catch (error) {
      console.warn("Stored printer pairing request is invalid", error);
    }
    if (!initialPairing) {
      localStorage.removeItem(PAIRING_STORAGE_KEY);
    }
  }

  if (!initialPairing) {
    const response = await fetch(`${PRINTER_SERVER_URL}/pairing-requests`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientName: "MenuPass Totem",
        origin: window.location.origin,
      }),
    });
    if (!response.ok) {
      throw new PrinterServerError(
        "pairing",
        `Printer access request failed (${response.status})`,
        response.status
      );
    }
    initialPairing = (await response.json()) as PairingAccessResponse;
    localStorage.setItem(PAIRING_STORAGE_KEY, JSON.stringify(initialPairing));
  }

  const pairing = await waitForApproval(
    initialPairing,
    Date.now() + PAIRING_TIMEOUT_MS
  );

  if (pairing.status !== "APPROVED" || !pairing.token) {
    localStorage.removeItem(PAIRING_STORAGE_KEY);
    throw new PrinterServerError(
      "pairing",
      `Printer access was ${pairing.status.toLowerCase()}`
    );
  }
  localStorage.setItem(TOKEN_STORAGE_KEY, pairing.token);
  localStorage.removeItem(PAIRING_STORAGE_KEY);
  return pairing.token;
};

const getToken = async (): Promise<string> => {
  const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (stored) return stored;
  if (!pairingPromise) {
    pairingPromise = requestAccess().finally(() => {
      pairingPromise = null;
    });
  }
  return pairingPromise;
};

const authorizedFetch = async (
  path: string,
  init: RequestInit = {}
): Promise<Response> => {
  let token = await getToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  let response = await fetch(`${PRINTER_SERVER_URL}${path}`, {
    ...init,
    headers,
  });
  if (response.status === 401) {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    token = await getToken();
    headers.set("Authorization", `Bearer ${token}`);
    response = await fetch(`${PRINTER_SERVER_URL}${path}`, {
      ...init,
      headers,
    });
  }
  return response;
};

const findAvailablePrinters = async (): Promise<Printer[]> => {
  const response = await authorizedFetch("/printers");
  if (!response.ok) {
    throw new PrinterServerError(
      response.status === 401 ? "authentication" : "discovery",
      `Printer discovery failed (${response.status})`,
      response.status
    );
  }
  return ((await response.json()) as { printers: Printer[] }).printers;
};

const getCurrentDefaultPrinter = async (): Promise<Printer | null> => {
  const available = await findAvailablePrinters();
  return available.find((printer) => printer.isDefault === true) || null;
};

export const initializePrinterServer = async (): Promise<boolean> => {
  try {
    return (await getCurrentDefaultPrinter()) !== null;
  } catch (error) {
    console.error("Printer server initialization failed", error);
    return false;
  }
};

const formatCurrency = (valueInCents: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  })
    .format(valueInCents / 100)
    .replace(/\u00a0/g, " ");

// The printer server currently sends UTF-8 bytes directly to ESC/POS printers.
// Transliterate receipt text until the server supports an explicit ESC/POS
// code page, preventing accented characters from being printed as mojibake.
const toPrinterSafeText = (value: string) =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const buildItemElements = (items: ReceiptOrder["items"]) =>
  items.reduce<Array<Record<string, unknown>>>((elements, item) => {
    const quantity = Math.max(0, Math.floor(item.amount));
    if (quantity === 0) return elements;

    const unitAmount = item.totalAmount / quantity;
    for (let index = 0; index < quantity; index += 1) {
      if (elements.length > 0) {
        elements.push({ type: "feed", lines: 1 });
      }
      elements.push({
        type: "row",
        left: `1x ${toPrinterSafeText(item.name)}`.slice(0, 20),
        right: formatCurrency(unitAmount).slice(0, 12),
      });
    }
    return elements;
  }, []);

const logPrintFailure = (
  result: Exclude<ReceiptPrintResult, { status: "submitted" }>
) => {
  const details = {
    orderReference: result.orderReference,
    stage: result.stage,
    httpStatus: result.httpStatus,
    message: result.message,
  };
  if (result.status === "failed") {
    console.error("Receipt printing failed", details);
  } else {
    console.warn("Receipt printing unavailable", details);
  }
};

const submitReceipt = async (
  receipt: ReceiptOrder,
  orderReference: string
): Promise<ReceiptPrintResult> => {
  try {
    const printer = await getCurrentDefaultPrinter();
    if (!printer) {
      const result: ReceiptPrintResult = {
        status: "printer-unavailable",
        orderReference,
        stage: "printer-selection",
        message: "No printer is marked as the system default",
      };
      logPrintFailure(result);
      return result;
    }

    const elements = [
      {
        type: "text",
        value: "MENUPASS",
        align: "CENTER",
        bold: true,
        doubleWidth: true,
        doubleHeight: true,
      },
      {
        type: "text",
        value: new Date().toLocaleString("pt-BR"),
        align: "CENTER",
      },
      { type: "separator", character: "-" },
      {
        type: "text",
        value: `Cliente: ${toPrinterSafeText(receipt.clientName)}`,
      },
      { type: "separator", character: "-" },
      ...buildItemElements(receipt.items),
      { type: "separator", character: "-" },
      {
        type: "row",
        left: "TOTAL",
        right: formatCurrency(receipt.totalAmount).slice(0, 12),
      },
      { type: "text", value: "Obrigado! Volte sempre.", align: "CENTER" },
      { type: "feed", lines: 4 },
      { type: "cut", mode: "PARTIAL" },
    ];
    const response = await authorizedFetch("/print-jobs", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Request-Timestamp": Date.now().toString(),
        "X-Request-Id": crypto.randomUUID(),
      },
      body: JSON.stringify({
        printerId: printer.id,
        copies: 1,
        idempotencyKey: `totem-order-${orderReference}-receipt`,
        document: { paperWidthMm: 58, elements },
      }),
    });
    if (!response.ok) {
      throw new PrinterServerError(
        "submission",
        `Automatic receipt failed (${response.status})`,
        response.status
      );
    }
    const result: ReceiptPrintResult = {
      status: "submitted",
      orderReference,
      printerId: printer.id,
    };
    completedOrderReferences.set(orderReference, result);
    return result;
  } catch (error) {
    const printerError =
      error instanceof PrinterServerError
        ? error
        : new PrinterServerError(
          "submission",
          error instanceof Error ? error.message : String(error)
        );
    const result: ReceiptPrintResult = {
      status: printerError.stage === "pairing" ? "pairing-required" : "failed",
      orderReference,
      stage: printerError.stage,
      httpStatus: printerError.httpStatus,
      message: printerError.message,
    };
    logPrintFailure(result);
    completedOrderReferences.set(orderReference, result);
    return result;
  }
};

export const printOrderReceiptIfAvailable = (
  receipt: ReceiptOrder,
  orderReference: string
): Promise<ReceiptPrintResult> => {
  if (!orderReference) {
    const result: ReceiptPrintResult = {
      status: "failed",
      orderReference,
      stage: "submission",
      message: "A stable order or transaction reference is required",
    };
    logPrintFailure(result);
    return Promise.resolve(result);
  }
  const completed = completedOrderReferences.get(orderReference);
  if (completed) return Promise.resolve(completed);
  const pending = pendingOrderReferences.get(orderReference);
  if (pending) return pending;

  const submission = submitReceipt(receipt, orderReference).finally(() => {
    pendingOrderReferences.delete(orderReference);
  });
  pendingOrderReferences.set(orderReference, submission);
  return submission;
};

export const useAutomaticOrderReceipt = (
  completed: boolean,
  orderReference: string | undefined,
  receipt: ReceiptOrder
) => {
  const receiptRef = useRef(receipt);
  receiptRef.current = receipt;

  useEffect(() => {
    if (!completed || !orderReference) return;
    const capturedReceipt = receiptRef.current;
    printOrderReceiptIfAvailable(capturedReceipt, orderReference);
  }, [completed, orderReference]);
};

export const usePrinter = () => {
  const [hasDefaultPrinter, setHasDefaultPrinter] = useState(false);

  useEffect(() => {
    getCurrentDefaultPrinter()
      .then((printer) => setHasDefaultPrinter(!!printer))
      .catch((error) => {
        console.error("Error connecting to MenuPass Printer Server", error);
        setHasDefaultPrinter(false);
      });
  }, []);

  const handlePrint = async (orderDetails: string, orderReference: string) => {
    const printer = await getCurrentDefaultPrinter();
    if (!printer) {
      throw new PrinterServerError(
        "printer-selection",
        "No printer is marked as the system default"
      );
    }
    const requestId = crypto.randomUUID();
    const response = await authorizedFetch("/print-jobs", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Request-Timestamp": Date.now().toString(),
        "X-Request-Id": requestId,
      },
      body: JSON.stringify({
        printerId: printer.id,
        copies: 1,
        idempotencyKey: `totem-order-${orderReference}-receipt`,
        document: {
          paperWidthMm: 58,
          elements: [
            { type: "text", value: orderDetails },
            { type: "feed", lines: 4 },
            { type: "cut", mode: "PARTIAL" },
          ],
        },
      }),
    });
    if (!response.ok) {
      throw new PrinterServerError(
        "submission",
        `Print request failed (${response.status})`,
        response.status
      );
    }
  };

  return {
    handlePrint,
    printers: hasDefaultPrinter ? ["Impressora padrão do sistema"] : [],
  };
};
